import {
  defaultLoadableState,
  useLoadableState
} from '@carvana/merch-react-hooks';
import { SearchBarContextProps } from '@carvana/merch-search-bar-legacy/SearchBarContext';
import deepmerge from 'deepmerge';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import type { LoadableState, SavedSearch } from '~/contracts/common';
import type { DeepPartial } from '~/types';

export type SavedSearchesContextType = {
  fetchSavedSearchTrigger: number;
  savedSearchesForLegacySearchBar: SearchBarContextProps['savedSearches'];
  savedSearches: LoadableState<SavedSearch[]>;
  triggerFetchSavedSearches: () => void;
};

export const defaultContext: SavedSearchesContextType = {
  fetchSavedSearchTrigger: 0,
  savedSearchesForLegacySearchBar: undefined,
  savedSearches: defaultLoadableState<SavedSearch[]>([]),
  triggerFetchSavedSearches() {}
};

const SavedSearchesContext =
  createContext<SavedSearchesContextType>(defaultContext);

export function formatSavedSearchesForLegacySearchBar(
  savedSearches: SavedSearch[]
): SearchBarContextProps['savedSearches'] {
  return savedSearches.map(({ cvnaId, name, savedSearchId }) => ({
    url: cvnaId,
    name,
    savedSearchId
  }));
}

export function SavedSearchesContextProvider({
  children
}: React.PropsWithChildren) {
  const [fetchSavedSearchTrigger, setTrigger] = useState<number>(
    defaultContext.fetchSavedSearchTrigger
  );
  const savedSearches = useLoadableState<SavedSearch[]>([]);
  const [savedSearchesForLegacySearchBar, setSavedSearchesForLegacySearchBar] =
    useState<SearchBarContextProps['savedSearches']>([]);

  useEffect(() => {
    if (savedSearches.data.length === 0) return;
    setSavedSearchesForLegacySearchBar(
      formatSavedSearchesForLegacySearchBar(savedSearches.data)
    );
  }, [savedSearches.data]);

  const triggerFetchSavedSearches = useCallback(() => {
    setTrigger(Date.now());
  }, []);

  const value = useMemo<SavedSearchesContextType>(
    () => ({
      fetchSavedSearchTrigger,
      savedSearchesForLegacySearchBar,
      savedSearches,
      triggerFetchSavedSearches
    }),
    [
      fetchSavedSearchTrigger,
      savedSearchesForLegacySearchBar,
      savedSearches,
      triggerFetchSavedSearches
    ]
  );

  return (
    <SavedSearchesContext.Provider value={value}>
      {children}
    </SavedSearchesContext.Provider>
  );
}

SavedSearchesContextProvider.displayName = 'SavedSearchesContextProvider';

export const generateSavedSearchesContext = (
  customState: DeepPartial<SavedSearchesContextType>
): SavedSearchesContextType =>
  deepmerge<SavedSearchesContextType, DeepPartial<SavedSearchesContextType>>(
    defaultContext,
    customState
  );

export function MockedSavedSearchesContextProvider({
  children,
  mockValue
}: React.PropsWithChildren<{
  mockValue: DeepPartial<SavedSearchesContextType>;
}>) {
  return (
    <SavedSearchesContext.Provider
      value={generateSavedSearchesContext(mockValue)}
    >
      {children}
    </SavedSearchesContext.Provider>
  );
}

export default SavedSearchesContext;

export function useSavedSearches() {
  const { savedSearches } = useContext(SavedSearchesContext);

  return savedSearches.data;
}

export function useSavedSearchesForLegacySearchBar() {
  const { savedSearchesForLegacySearchBar } = useContext(SavedSearchesContext);

  return savedSearchesForLegacySearchBar;
}

export function useSavedSearchesIsLoaded(): boolean {
  const { savedSearches } = useContext(SavedSearchesContext);

  return savedSearches.isLoaded;
}
